import { WorkflowPreparationSubmitRequestForm } from '@app/generated/contracts/workflow-preparation-submit-request.generated';
import { editableFormPayload, validateGeneratedForm, type FormIssues, type GeneratedFormValues } from '@template-ui/helpers/generated-form.helpers';
import type { WorkflowClientAction, WorkflowClientCommandModule } from './workflow-command-registry';
import { providedLookupRevisions, resolveProvidedLookupFields, workflowFormLookupBindingKey } from './workflow-form-lookup';

function preparationInputIssues(action: WorkflowClientAction, values: GeneratedFormValues): FormIssues {
  const field = action.form?.configuration?.fields.find(field => field.name === 'workSummary');
  const value = values['workSummary'];
  // The retained Request v1 handoff trims summary text, so padded codes cannot round-trip.
  return field?.control === 'select' && typeof value === 'string' && value !== value.trim()
    ? { workSummary: 'This choice cannot be submitted for review because its stored value contains leading or trailing whitespace.' }
    : {};
}

/** Preparation v2 owns one closed scalar lookup transport; legacy preparation stays v1. */
export const workflowPreparationLookupCommandModule: WorkflowClientCommandModule = {
  code: 'platform.preparation.lookup', version: 1, commands: [{
    key: { provider: 'workflow', kind: 'command', code: 'workflow.preparation.submit', version: 2 },
    form: WorkflowPreparationSubmitRequestForm,
    endpointSuffix: '/preparation/v2',
    submitLabel: 'Submit work for review',
    submissionHint: 'Saves your work and opens a fresh independent review.',
    confirmation: 'Work submitted for review.',
    supportsProvidedLookups: true,
    validateInput: preparationInputIssues,
    bindContext: context => ({ occurrenceId: context.occurrenceId, expectedETag: context.stepETag }),
    draftContextKey: action => {
      const binding = action.form?.configuration?.fields.find(field => field.name === 'workSummary')?.lookup;
      return binding ? JSON.stringify(['provided', WorkflowPreparationSubmitRequestForm.contract, 'workSummary', workflowFormLookupBindingKey(binding)]) : null;
    },
    serializeInput: (form, values, action) => {
      if (!action?.form || Object.keys(validateGeneratedForm(form, values)).length || Object.keys(preparationInputIssues(action, values)).length) return null;
      const resolved = resolveProvidedLookupFields(form, action.form.configuration, action.form.lookupFields);
      const revisions = resolved ? providedLookupRevisions(form, action.form.configuration, resolved.revisions, values) : null;
      if (!revisions || Object.keys(revisions).some(name => name !== 'workSummary')) return null;
      const input = editableFormPayload(form, values);
      return { ...input, ...(revisions['workSummary'] ? { workSummaryLookupRevision: revisions['workSummary'] } : {}) };
    },
  }],
};
